import { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import { CorpAdminContext } from '../../../store';
import {
    DELETE_EXTERNAL_IDENTIFIER,
    GET_EXTERNAL_IDENTIFIERS,
    GET_EXTERNAL_IDENTIFIER_DEFINITIONS,
    UPSERT_EXTERNAL_IDENTIFIER,
} from '../../../graphql';
import { buildIdentifierObject, resolveSaveAction } from './utils';

// Loads the signed-in admin org's external-identifier definitions and this client's stored values,
// and lets the admin set any of them on the client. Keys come from external_identifier_definitions
// (never hardcoded) and are not filtered by the client's account type.
export const useExternalIdentifiers = ({ clientId }) => {
    const { organization_id } = useClientUser();
    const { callbacks } = useContext(CorpAdminContext);
    const setNotification = callbacks?.setNotification;

    const skip = !organization_id || !clientId;

    const { data: defsData, loading: defsLoading } = useQuery(GET_EXTERNAL_IDENTIFIER_DEFINITIONS, {
        variables: { organization_id },
        skip: !organization_id,
        fetchPolicy: 'cache-and-network',
    });

    const variables = useMemo(() => ({ organization_id, client_id: clientId }), [organization_id, clientId]);
    const { data: valuesData, loading: valuesLoading, refetch } = useQuery(GET_EXTERNAL_IDENTIFIERS, {
        variables,
        skip,
        fetchPolicy: 'cache-and-network',
    });

    const definitions = defsData?.external_identifier_definitions || [];
    const existingByKey = useMemo(() => {
        const map = {};
        (valuesData?.external_identifiers || []).forEach((row) => {
            map[row.key] = row;
        });
        return map;
    }, [valuesData]);

    // Only holds keys the user has edited; the stored value shows through for the rest.
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState(false);

    const [upsertIdentifier] = useMutation(UPSERT_EXTERNAL_IDENTIFIER);
    const [deleteIdentifier] = useMutation(DELETE_EXTERNAL_IDENTIFIER);

    const rows = definitions.map((def) => {
        const existing = existingByKey[def.key] || null;
        const value = def.key in drafts ? drafts[def.key] : existing?.value || '';
        const { action } = resolveSaveAction({ value, existing });
        return { key: def.key, label: def.label, description: def.description, value, existing, dirty: action !== 'noop' };
    });

    const dirtyRows = rows.filter((row) => row.dirty);

    const onChange = (key, next) => setDrafts((prev) => ({ ...prev, [key]: next }));

    const save = async () => {
        if (skip || busy || !dirtyRows.length) return;
        setBusy(true);
        try {
            for (const row of dirtyRows) {
                const { action } = resolveSaveAction({ value: row.value, existing: row.existing });
                if (action === 'delete') {
                    await deleteIdentifier({ variables: { id: row.existing.id } });
                } else if (action === 'upsert') {
                    await upsertIdentifier({
                        variables: {
                            object: buildIdentifierObject({
                                organization_id,
                                clientId,
                                key: row.key,
                                label: row.label,
                                value: row.value,
                            }),
                        },
                    });
                }
            }
            await refetch();
            setDrafts({});
            setNotification?.({ severity: 'success', message: 'Identifiers saved' });
        } catch (err) {
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to save identifiers' });
        } finally {
            setBusy(false);
        }
    };

    return {
        state: {
            rows,
            loading: defsLoading || valuesLoading,
            busy,
            canSave: !skip && dirtyRows.length > 0,
            unavailable: !defsLoading && definitions.length === 0,
        },
        callbacks: { onChange, save },
    };
};
